import {
  breakpoints,
  Chip,
  Col,
  DropdownMenu,
  Icon,
  type IconName,
  type MenuItem,
  ScrollArea,
  Tooltip,
  useTheme,
} from "@rally/ui-kit";
import { say } from "../../localization/say.ts";
import { type FilterName, THE_CHIPS, THE_REST } from "./sift.ts";

export const THE_HEAD_IS = 44;

export const GLYPH_OF: Record<FilterName, IconName> = {
  all: "messages-square",
  unread: "mail",
  archived: "archive",
  pinned: "pin",
  snoozed: "alarm-clock",
  muted: "volume-off",
};

const wordOf = (which: FilterName): string =>
  ({
    all: say("sift.all"),
    unread: say("sift.unread"),
    archived: say("sift.archived"),
    pinned: say("sift.pinned"),
    snoozed: say("sift.snoozed"),
    muted: say("sift.muted"),
  })[which];

export function Sifter({
  counts,
  filter,
  onFilter,
  unreadFirst,
  onUnreadFirst,
  onNewProject,
}: {
  counts: Record<FilterName, number>;
  filter: FilterName;
  onFilter: (filter: FilterName) => void;
  unreadFirst: boolean;
  onUnreadFirst: (on: boolean) => void;
  onNewProject: () => void;
}) {
  const theme = useTheme();
  const narrow = theme.isAtMost(breakpoints.phone);

  const numberOn = (which: FilterName): string | null =>
    which === "archived" ? null : String(counts[which]);

  const chip = (which: FilterName) => {
    if (counts[which] === 0) return null;
    const on = filter === which;
    const count = numberOn(which);
    return (
      <Chip
        key={which}
        icon={GLYPH_OF[which]}
        selected={on && which !== "all"}
        onPress={() => onFilter(on && which !== "all" ? "all" : which)}
        testID={`chip-${which}`}
      >
        {count === null ? wordOf(which) : `${wordOf(which)} ${count}`}
      </Chip>
    );
  };

  const menu: MenuItem[] = [
    ...THE_REST.filter((which) => counts[which] > 0).map((which) => ({
      label: wordOf(which),
      icon: GLYPH_OF[which],
      hint: String(counts[which]),
      onPress: () => onFilter(filter === which ? "all" : which),
    })),
    {
      label: say("sift.unreadFirst"),
      icon: unreadFirst ? ("check" as IconName) : ("minus" as IconName),
      onPress: () => onUnreadFirst(!unreadFirst),
    },
  ];

  return (
    <Col
      testID="sift"
      style={{
        flexDirection: "row",
        alignItems: "center",
        height: THE_HEAD_IS,
        gap: theme.space[1],
      }}
    >
      <Col style={{ flex: 1, minWidth: 0 }}>
        <ScrollArea
          horizontal
          contentContainerStyle={{
            flexDirection: "row",
            alignItems: "center",
            gap: theme.space[1],
          }}
          style={{ flexGrow: 0 }}
        >
          {THE_CHIPS.map((which) => chip(which))}
        </ScrollArea>
      </Col>

      <DropdownMenu
        align={narrow ? "left" : "right"}
        width={220}
        items={menu}
        accessibilityLabel={say("sift.more")}
        testID="sift-more"
        trigger={
          <Col style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}>
            <Icon name="sliders-horizontal" size={16} color="text.tertiary" />
          </Col>
        }
      />
      <Tooltip
        content={say("chat.newProject")}
        accessibilityLabel={say("chat.newProject")}
        testID="new-project"
        onPress={onNewProject}
      >
        <Col style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}>
          <Icon name="folder-plus" size={18} color="text.secondary" />
        </Col>
      </Tooltip>
    </Col>
  );
}
